/**
 * What is left of the Office Console's ported seed data.
 *
 * The crew and their certificates used to live here, duplicating what the
 * database already held. They now come from `GET /projects/:id/crew` and this
 * file keeps only what has no endpoint yet: the site manager, the company,
 * the vendors and the day's schedule.
 *
 * Consumers go through the hooks in `@/hooks/use-site`, so replacing the rest
 * will not touch any screen either.
 *
 * **The ids are uuids**, because the tables these stand in for are uuid-keyed —
 * `people`, `vendors`; `schedule_entries` was dropped unread on 2026-10-18
 * (drift W-020, W-277), so the schedule here stands in for no table. See
 * `project_intelligence/data-model/0006-ids-are-uuids.md`. A fixture still
 * spelling `"trina"` would quietly teach the next reader that vendor ids are
 * slugs, and these are the first screens anyone opens. They are literals rather
 * than `crypto.randomUUID()` so an id is stable across reloads, which the React
 * keys and the vendor lookup depend on.
 */
import type { ScheduleItem, SiteManager, Vendor } from "./types";

const VENDOR_TRINA = "5e2a94c7-3f18-4b60-9d7a-8c1e0b42f5d3";
const VENDOR_K2 = "7c81f3a9-64b2-4e05-8a19-3d6f2c07b184";
const VENDOR_SMA = "b93e0d47-25c8-4f1a-9026-7ea4b1c8d350";

export const SITE_MANAGER: SiteManager = {
  id: "0f6d3b81-9a24-4c17-8e35-2b7c0d918af6",
  name: "Jamie Brooks",
  role: "Site manager",
  initials: "JB",
};

export const COMPANY = {
  name: "Meridian Solar",
  role: "Solar EPC contractor",
} as const;

export const VENDORS: Vendor[] = [
  {
    id: VENDOR_TRINA,
    name: "Trina Solar",
    product: "TSM-DEG21C panels",
    short: "Trina",
  },
  {
    id: VENDOR_K2,
    name: "K2 Systems",
    product: "SingleRail 2.0 mounting",
    short: "K2",
  },
  {
    id: VENDOR_SMA,
    name: "SMA",
    product: "Sunny Tripower inverters",
    short: "SMA",
  },
];

export const SCHEDULE: ScheduleItem[] = [
  {
    id: "1d4f7a20-8c93-4e6b-b158-0af52c96e731",
    task: "Install Trina panels",
    detail: "Sector B1 · rows 23–28",
    sector: "B1",
    vendorId: VENDOR_TRINA,
    crewSize: 8,
  },
  {
    id: "6a05c318-7be4-42d9-8f70-914d3e2ba5c8",
    task: "Mount K2 rails",
    detail: "Sector B2 · rows 11–15",
    sector: "B2",
    vendorId: VENDOR_K2,
    crewSize: 6,
  },
  {
    id: "e27b58d0-4a16-4c83-95ef-3b0c81f7d624",
    task: "SMA inverter bank A pre-commission",
    detail: "Inverter yard · continue",
    sector: "Inverter yard",
    vendorId: VENDOR_SMA,
    crewSize: 2,
  },
];
